import { useState, useEffect } from 'react';
import { faFile, faVideo, faImage, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

import useNotifications from '@lib/hooks/notifications';
import { If, Slot, createComponent } from '@lib/util/templateHelpers';
import useForm from '@lib/hooks/form';
import shared from '@lib/components';
const { 
  Form, 
  Button, 
  Input, 
  Icon,
  Field,
  Flex
} = shared;

import styles from './index.module.scss';
import { useAuthStore } from '@/stores/auth';
import fileApi from '@/apis/drupal/file';

// 307200

export default createComponent('UploadFileForm', { styles }, function UploadFileForm ({ className }, props) {
  const schema = {
    files: {
      value: undefined,
      schema: {
        type: Form.FIELD_ANY
      }
    }
  };

  let apiMethod;
  if (props.accept === Input.FILE_ACCEPT_IMAGE) apiMethod = 'uploadImage';
  else if (props.accept === Input.FILE_ACCEPT_VIDEO) apiMethod = 'uploadVideo';
  else apiMethod = 'uploadDocument';

  let icon;
  if (props.accept === Input.FILE_ACCEPT_IMAGE) icon = faImage;
  else if (props.accept === Input.FILE_ACCEPT_VIDEO) icon = faVideo;
  else icon = faFile;

  const form = useForm(schema);
  const { notify } = useNotifications();
  const [ state ] = useAuthStore();
  const [ loading, setLoading ] = useState(false);

  useEffect(() => {
    if (!state.ready) return;
  }, [state]);

  const showPreview = 
    props.accept?.toLowerCase().includes('image') || 
    props.accept?.toLowerCase().includes('video');

  const uploadError = (err) => {
    form.resetForm();

    if (err === 'size_too_big') {
      return notify(`File size must be no more than ${Math.round(props.maxFileSize / 1024)}kb.`)
    }
    if (err === 'incorrect_image_dimensions') {
      return notify(`Image must be exactly ${props.imageSize[0]}px by ${props.imageSize[1]}px.`)
    }
    if (err === 'incorrect_video_dimensions') {
      return notify(`Video must be at least ${props.videoSize[0]}px by ${props.videoSize[1]}px and the same aspect ratio.`)
    }
  };

  const [ hasUploaded, setHasUploaded ] = useState(false);

  const submit = async () => {
    const { data, hasErrors } = form.validate();
    if (hasErrors) return;

    setLoading(true);

    const file = data.files[0];
    if (!file) {
      notify('Please select a file to upload.');
      setLoading(false);
      return;
    }
    const headerEndIndex = file.data.indexOf(',');
    const fileData = file.data.slice(headerEndIndex + 1);
    const result = await fileApi[apiMethod](file.name, fileData, state.token, state.auth.uid);
  
    if (result.error) {
      notify('There was a problem uploading your file. Please try again later.');
      if (props.onError) props.onError(result.error); 
      setHasUploaded(false);
    } else  {
      notify('Your file was uploaded successfully!');
      if (props.onSuccess) props.onSuccess(result);
      setHasUploaded(true);
    }

    setLoading(false);
  };

  const imagePreviewSrc = 
    Input.FILE_ACCEPT_IMAGE 
    && (
      form.data?.files?.length 
      && form.data?.files[0].data
    ) 
    || props.previewUrl;

  const videoType = 
    props.accept === Input.FILE_ACCEPT_VIDEO 
    && form.data?.files?.length 
    && form.data.files[0].data.split(';')[0].split(':')[1]; // extracts the format from the base64 datastring header

  return (
    <Form className={className} schema={schema} onSubmit={submit}>
      {
        If(showPreview && (props.previewUrl || form.data?.files?.length), () => (
        <Field>
          {
            If(props.accept === Input.FILE_ACCEPT_IMAGE, () => (
              <img className='UploadFileForm__Preview' src={imagePreviewSrc} />
            ))
            .Else(() => (
              <video className='UploadFileForm__Preview' controls>
                {
                  If(form.data?.files?.length, () => (
                    <source type={videoType} src={form.data.files[0].data} />
                  ))
                  .Else(() => (
                    <source type={props.previewVideoFormat} src={props.previewUrl} />
                  ))
                  .EndIf()
                }
              </video>
            ))
            .EndIf()
          }
        </Field>
        ))
        .EndIf()
      }
      <Input.UncontrolledField 
        name='files'
        form={form}
        type={Input.FILE} 
        accept={props.accept} 
        fileFormat={Input.FILE_FORMAT_BASE64}
        maxFileSize={props.maxFileSize}
        imageSize={props.imageSize}
        videoSize={props.videoSize}
        onFileError={uploadError}
      >
        <Slot name='icon'>
          {
            If(props.accept === Input.FILE_ACCEPT_IMAGE, () => (
              <Icon.FA icon={faImage} />
            ))
            .ElseIf(props.accept === Input.FILE_ACCEPT_VIDEO, () => (
              <Icon.FA icon={faVideo} />
            ))
            .Else(() => (
              <Icon.FA icon={faFile} />
            ))
            .EndIf()
          }
        </Slot>
      </Input.UncontrolledField>
      <Flex alignCenter>
        <Button primary submit disabled={form.hasErrors} loading={loading}>{props.children || 'Upload File'}</Button>
        {
          If(hasUploaded, () => (
            <Flex className='--padMd1__l'><Icon.FA icon={faCheckCircle} /></Flex>
          ))
          .EndIf()
        } 
      </Flex>
    </Form>
  );
});
